import React, { useEffect, useState } from 'react';
import * as R from 'ramda';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { useMediaQuery } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import AffiliateDropdown from 'feature/dispatching/components/sidebar/AffiliateDropdown';
import ViewByDropdown from 'feature/dispatching/components/sidebar/ViewByDropdown';
import FiltersSkeleton from 'feature/dispatching/components/sidebar/FiltersSkeleton';
import MobileFilters from 'feature/dispatching/components/sidebar/MobileFilters';
import SelectorCard from 'feature/dispatching/components/sidebar/SelectorCard';
import SelectorCardCount from 'feature/dispatching/components/sidebar/SelectorCardCount';
import SelectorSkeleton from './SelectorSkeleton';
import { useViewLoadsSummaryByDestinationQuery } from '../loadsEndpoints';

const sidebarStyles = {
  bgcolor: 'surface.white',
  border: 1,
  borderBottom: 0,
  borderColor: 'graphic.light',
  borderLeft: 0,
  borderTop: 0,
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  pb: 2,
  position: 'fixed',
  // eslint-disable-next-line
  top: { xs: 56, sm: 64, md: 64, lg: 48 },
  // eslint-disable-next-line
  width: { xs: 1, sm: 1, md: 300, lg: 300 },
};

const scrollbarStyles = {
  '&::-webkit-scrollbar': {
    display: 'none',
  },
  overflowY: 'scroll',
  pb: 4,
  scrollbarWidth: 'none',
};

const ViewSummaryByDestination = () => {
  const navigate = useNavigate();
  const { viewById } = useParams();
  const mdAndDown = theme => theme.breakpoints.down('md');
  const mdDown = useMediaQuery(mdAndDown, { noSsr: true });
  const [selectorsFilter, setSelectorsFilter] = useState('');

  const equalsViewById = R.equals(Number(viewById));

  const { data: loadsQuery = [], isLoading } = useViewLoadsSummaryByDestinationQuery();

  // prettier-ignore
  const destinationFilter = R.compose(
    R.includes(R.toUpper(selectorsFilter)),
    R.toUpper,
    R.prop('name')
  )
  const filteredDestinations = loadsQuery.filter(destinationFilter);

  // prettier-ignore
  const handleSelectorsFilterChange = R.compose(
    setSelectorsFilter,
    R.path(['target', 'value'])
  );

  // prettier-ignore
  const handleSelectorClick = R.curryN(2, R.compose(
    navigate,
    R.concat('../loads/destination/'),
    R.toString
  ));

  // prettier-ignore
  const handleAffiliateChange = R.compose(
    setSelectorsFilter,
    R.always('')
  );

  useEffect(() => {
    // prettier-ignore
    const autoSelectFirst = R.and(
      R.not(mdDown), R.and(
      R.isNil(viewById),
      R.gt(loadsQuery.length, 0)
    ));

    if (autoSelectFirst) {
      const [{ destinationId }] = loadsQuery;
      navigate(`../loads/destination/${destinationId}`, { replace: true });
    }
  }, [loadsQuery, mdDown, navigate, viewById]);

  const filters = (
    <Stack gap={2} sx={{ px: 2, py: 3 }}>
      <AffiliateDropdown onChange={handleAffiliateChange} />
      <ViewByDropdown module="loads">
        <MenuItem value="date">Date Created</MenuItem>
        <MenuItem value="destination">Destination</MenuItem>
        <MenuItem value="location">Location</MenuItem>
        <MenuItem value="driver">Driver</MenuItem>
      </ViewByDropdown>
      <TextField
        label="Filter Destination"
        onChange={handleSelectorsFilterChange}
        value={selectorsFilter}
      />
    </Stack>
  );

  if (isLoading)
    return (
      <Box sx={sidebarStyles}>
        {mdDown ? (
          <MobileFilters>
            <FiltersSkeleton />
          </MobileFilters>
        ) : (
          <FiltersSkeleton />
        )}
        <Divider />
        <SelectorSkeleton />
        <SelectorSkeleton />
        <SelectorSkeleton />
      </Box>
    );

  return (
    <Box sx={sidebarStyles}>
      {mdDown ? <MobileFilters>{filters}</MobileFilters> : filters}
      <Divider />
      <Box sx={scrollbarStyles}>
        {filteredDestinations.map(
          ({ destinationId, name, draftLoadsCount, submittedLoadsCount }) => (
            <SelectorCard
              key={destinationId}
              onClick={handleSelectorClick(destinationId)}
              selected={equalsViewById(destinationId)}
              title={R.toUpper(name)}
            >
              <SelectorCardCount isActive label="Submitted Loads" value={submittedLoadsCount} />
              <SelectorCardCount label="Draft Loads" value={draftLoadsCount} />
            </SelectorCard>
          )
        )}
      </Box>
    </Box>
  );
};

export default ViewSummaryByDestination;
